import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ActivatingEvent,
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
  basicFeelingsEn,
  basicFeelingsRu,
  bodyPartsEn,
  bodyPartsRu,
  DayReminders,
  dbCols,
  getValidators,
  Thought,
  translationEn,
} from '@cc/schema';
import {
  numberToOnesAndZerosArrayOfArrays,
  onesAndZerosArrayOfArraysToNumber,
} from '@cc/utils';
import { Create } from '@refinedev/antd';
import { merge } from 'lodash';

import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Form,
  Input,
  InputNumber,
  Result,
  Select,
  Slider,
  Table,
  Typography,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustom,
  useCustomMutation,
  useTranslate,
} from '@refinedev/core';

import * as Sentry from '@sentry/react';
import { useActor } from '@xstate/react';
import { RuleObject } from 'antd/lib/form';
import { useWatch } from 'antd/lib/form/Form';
import { set } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash';
import qs from 'qs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'rooks';
import { TherapistActivityReactContext } from '../../App';
import { EsCardReminders } from '../../components/EsCardReminders';
import { withLegacyDaySlotsSilenced } from '../../components/EsCardReminders/legacyDaySlots';
import { esThoughtTestBatch } from './esThoughtTest';
import { DateFnsTimePicker } from '../../components/Pickers';
import { SelectClient } from '../../components/SelectClient';
import { getYupSyncValidator } from '../../helpers';
import { useFormSubmitDisabledAnyTouched } from '../../hooks';
import { useClientRoster } from '../../hooks/client-options';
import { useClientEra } from '../../hooks/useClientEra';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';

const { Text, Title } = Typography;

/** What `thoughtTestDraft` answers — the five display fields plus the answers already recorded. */
type ThoughtTestDraft = {
  subject: string;
  thoughtTestId: string;
  activatingEventId?: string | null;
  description: string;
  emotions?: { id: number; strength: number }[];
  feelings?: number[];
  bodyPartsFeelings?: number;
  behaviors?: string;
  automaticThought: string;
  thoughtBelief: number;
  resumeStep: string;
  /** Absent means the step never happened — the log's answer, not a row's default. */
  thoughtsFor?: string[];
  thoughtsForAlternativeThoughts?: (string | null)[];
  thoughtsAgainst?: string[];
  cognitiveDistortions?: string[];
  realisticThoughts?: { thought: string; thoughtBelief: number }[];
  result?: {
    initialThoughtBelief?: number;
    initialNegativeEmotionStrength?: number;
  };
  thought?: {
    thoughtsFor?: string[];
    thoughtsForAlternativeThoughts?: (string | null)[];
    thoughtsAgainst?: string[];
    cognitiveDistortions?: string[];
    realisticThoughts?: { thought: string; thoughtBelief: number }[];
    result?: {
      initialThoughtBelief?: number;
      initialNegativeEmotionStrenght?: number;
    };
  } | null;
};
const { Panel } = Collapse;
const { OptGroup, Option } = Select;

export const emptyBasicThought = {
  thought: '',
  thoughtBelief: 50,
};

type CreateActivatingEventForm = {
  // Activating Event
  description: string; // Activating event
  emotions: ActivatingEvent['emotions'];
  bodyPartsFeelings: ActivatingEvent['bodyPartsFeelings'];
  feelings: ActivatingEvent['feelings'];
  behaviors: ActivatingEvent['behaviors'];

  // Thought
  automaticThought: string;
  thoughtBelief: number;
  // thoughtsFor: string[];
  // thoughtsForAlternativeThoughts: string[] | null[] | (string | null)[];
  thoughtsFor: {
    for: string;
    alternativeThought: string | null;
  }[];
  thoughtsAgainst: string[];
  cognitiveDistortions?: number[];
  realisticThoughts: (typeof emptyBasicThought)[];
  result: {
    initialThoughtBelief: number;
    initialNegativeEmotionStrenght: number;
  };
  cardName: string;
  categoriesIds: number[] | null;

  // Reminders
  dayReminders?: DayReminders;
  // locationReminders?: LocationReminder[];
};

export const CreateThought: React.FC<IResourceComponentsProps> = () => {
  const [form] = Form.useForm<CreateActivatingEventForm>();

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const translate = useTranslate();

  const { portalDysThoughtTestValidator } = useMemo(
    () => getValidators(translate),
    [i18n.languages[0]],
  );

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const validationRules = getYupSyncValidator(
    translate,
    portalDysThoughtTestValidator,
  ) as RuleObject;

  const [isCardFlipped, setIsCardFlipped] = useState(false);

  // Note: as RuleObject is silenciing Antd since it seems to have wrong types

  const {
    handleFormChange,
    isDisabled: isSubmitDisabled,
    fieldsCount,
  } = useFormSubmitDisabledAnyTouched(form);

  // the WHOLE practice roster (P011) — this page only waits on it (the client picker below reads
  // the same shared roster through `SelectClient`)
  const { clientsLoading } = useClientRoster();

  const {
    mutate,
    data: createData,
    isLoading: createLoading,
    error: createError,
  } = useCustomMutation<{ thought: Thought }>();

  const baseEmotions = useMemo(() => {
    const baseEmotions = i18n.languages[0]?.startsWith('ru')
      ? baseEmotionsRu
      : baseEmotionsEn;

    return baseEmotions.map((e) => ({
      ...e,
      children: e.children.map((ch) => ({ ...ch, strength: 50 })),
    }));
  }, [i18n.languages[0]]);

  const flattenedEmotions = useMemo(() => {
    return baseEmotions.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseEmotionsEn)[0]['children'],
    );
  }, [baseEmotions]);

  const baseFeelings = i18n.languages[0]?.startsWith('ru')
    ? baseFeelingsRu
    : baseFeelingsEn;

  const headerFeelings = i18n.languages[0]?.startsWith('ru')
    ? basicFeelingsRu
    : basicFeelingsEn;

  const bodyParts = i18n.languages[0]?.startsWith('ru')
    ? bodyPartsRu
    : bodyPartsEn;

  /**
   * `preserve: true` — WATCH THE STORE, NOT THE REGISTERED FIELDS.
   *
   * By default `useWatch` follows a value only while a `Form.Item` for it is mounted. Every section
   * of this form is a collapsed `Collapse` panel, and antd does not render a panel's children until
   * it is opened — so with the emotions panel shut, nothing is registered under `emotions`, and a
   * value poured in by `setFieldsValue` (a RESUME) never reached these watches. `setEmotions` sat at
   * `[]` while the store held the client's emotions; the moment the panel opened, `Form.List` mounted
   * one row per emotion and its label read `setEmotions[0].id` off an empty array — a TypeError in
   * `<Field>`, and a form that could not be opened past section 2 (found by the cross-surface spec
   * while fixing the founder's second report, 2026-09-11).
   *
   * A fresh create never hit it: values only entered the store through mounted fields, which were
   * registered by definition. This form had simply never been pre-filled from outside before.
   */
  const setEmotions: { id: number; strength: number }[] =
    useWatch('emotions', { form, preserve: true }) || [];
  const bodyPartsFeelings =
    useWatch('bodyPartsFeelings', { form, preserve: true }) || [];
  const dayReminders = useWatch('dayReminders', form);

  const bodyPartsAsArray = useMemo(
    () =>
      numberToOnesAndZerosArrayOfArrays(
        headerFeelings.length,
        bodyParts.length,
        // @ts-ignore
        bodyPartsFeelings,
      ),
    [bodyPartsFeelings],
  );

  const feelingsColumns = useMemo(() => {
    return [
      {
        title: '',
        dataIndex: 'feelingName',
        render: (text: string, record: any) => (
          <div
            style={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 40,
            }}
          >
            {text}
          </div>
        ),
      },
      ...headerFeelings.map((feeling) => {
        return {
          title: feeling,
          dataIndex: feeling,
          render: (value: any, record: any) => {
            return (
              <Checkbox
                checked={!!value}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const copiedBodyPartsAsArray = cloneDeep(bodyPartsAsArray);
                  const columnIdx = headerFeelings.indexOf(feeling);
                  copiedBodyPartsAsArray[record.key][columnIdx] = checked
                    ? 1
                    : 0;
                  const encodedArray = onesAndZerosArrayOfArraysToNumber(
                    copiedBodyPartsAsArray,
                  );

                  form.setFieldValue('bodyPartsFeelings', encodedArray);
                }}
              />
            );
          },
        };
      }),
    ];
  }, [headerFeelings, bodyPartsAsArray]);

  const feelingsData = useMemo(() => {
    return bodyPartsAsArray.map((row, i) => {
      return Object.assign(
        {},
        ...[
          { key: i },
          {
            feelingName: bodyParts[i],
          },
          ...row.map((col, j) => {
            return {
              [headerFeelings[j]]: col,
            };
          }),
        ],
      );
    });
  }, [feelingsColumns, bodyPartsAsArray]);

  const dayRemindersColumns = useMemo(() => {
    return [
      {
        title: '',
        dataIndex: 'period',
        render: (period: string) => {
          return (
            <div style={{ minWidth: 60 }}>
              <Text strong>{translate(`createThought.${period}`)}</Text>
            </div>
          );
        },
      },
      {
        title: '',
        dataIndex: 'time',
        render: (
          time: DayReminders['day']['time'],
          record: DayReminders['day'],
        ) => {
          return (
            <DateFnsTimePicker
              style={{
                minWidth: i18n.languages[0]?.startsWith('ru') ? 60 : 100,
              }}
              defaultValue={set(new Date(), {
                hours: time.hours,
                minutes: time.minutes,
              })}
              format={i18n.languages[0]?.startsWith('ru') ? 'HH:mm' : 'hh:mm a'}
              onChange={(date: Date | Date[]) => {
                if (Array.isArray(date)) return;
                const hours = date?.getHours() || 0;
                const minutes = date?.getMinutes() || 0;

                const copiedDayReminders = {
                  ...form.getFieldValue('dayReminders'),
                };
                copiedDayReminders[
                  // @ts-ignore
                  record.key
                ].time = {
                  hours,
                  minutes,
                };
                form.setFieldValue('dayReminders', copiedDayReminders);
              }}
            />
          );
        },
      },
      {
        title: '',
        dataIndex: 'enabled',
        render: (enabled: boolean, record: DayReminders['day']) => {
          return (
            <Checkbox
              checked={enabled}
              onChange={(e) => {
                const checked = e.target.checked;

                const copiedDayReminders = {
                  ...form.getFieldValue('dayReminders'),
                };
                copiedDayReminders[
                  // @ts-ignore
                  record.key
                ].enabled = checked;
                form.setFieldValue('dayReminders', copiedDayReminders);
              }}
            />
          );
        },
      },
    ];
  }, [dayReminders]);

  const dayRemindersData = useMemo(() => {
    return Object.keys(dayReminders || {}).map((period) => {
      return {
        key: period,
        period,
        // @ts-ignore
        ...(dayReminders?.[period] || {}),
      };
    });
  }, [dayReminders]);

  const parsedSearch = qs.parse(
    location.search.charAt(0) === '?'
      ? location.search.slice(1)
      : location.search,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (parsedSearch.clientId as string) ||
      therapistActivityState.context.filterClientId ||
      '',
  );

  // R71: the era decides which reminder surface this page offers — see the section-11 comment
  const { isEsClient } = useClientEra(clientId, 'reminders');
  /**
   * …and the THOUGHT TESTING era decides where the test itself is written. A separate question from
   * the one above: the two domains are flipped per subject and can differ, so asking the reminder
   * gate about the thought test would route by the wrong answer the moment they do.
   */
  const { isEsClient: isEsThoughtTesting } = useClientEra(
    clientId,
    'thoughtTesting',
  );

  /**
   * CONTINUING AN UNFINISHED TEST (founder 2026-09-11). `?thoughtTestId=` turns this screen into a
   * resume: the same boundary, pre-filled with everything already answered, so the remaining steps
   * append rather than starting a second record. Absent, it is an ordinary new test.
   */
  const resumeThoughtTestId = (parsedSearch.thoughtTestId as string) || '';

  const { data: draftData } = useCustom<{ thoughtTestDraft: ThoughtTestDraft }>(
    {
      url: '',
      method: 'get',
      meta: {
        operation: 'thoughtTestDraft',
        /**
         * THIS LIST IS THE QUERY. The portal does not use the shared `ThoughtTestDraft` document in
         * `@cc/schema` — Refine composes the selection from these names — so a field the draft
         * gained on the server is invisible here until it is ADDED here.
         *
         * That is exactly what shipped broken on 2026-09-11 (founder, prod, second report): the
         * draft was widened with the situation and every reasoning step, the pre-fill below was
         * rewritten to read them, the API validated them live — and this list still asked for the
         * original five, so `draft.emotions` was `undefined` on every resume and the form stayed
         * blank over answers the log held. The pre-fill was reading fields the query never sent for.
         *
         * Keep it the mirror of `documents/thoughtTesting.gql › ThoughtTestDraft`, minus `thought`:
         * the portal reads nothing from the legacy row any more (the log is more precise — it can
         * tell "not answered" from "answered with the default", the row cannot).
         */
        fields: [
          'subject',
          'thoughtTestId',
          'activatingEventId',
          'description',
          { emotions: ['id', 'strength'] },
          'feelings',
          'bodyPartsFeelings',
          'behaviors',
          'automaticThought',
          'thoughtBelief',
          'resumeStep',
          'thoughtsFor',
          'thoughtsForAlternativeThoughts',
          'thoughtsAgainst',
          'cognitiveDistortions',
          { realisticThoughts: ['thought', 'thoughtBelief'] },
          {
            result: ['initialThoughtBelief', 'initialNegativeEmotionStrength'],
          },
        ],
        variables: {
          thoughtTestId: {
            name: 'thoughtTestId',
            type: 'ID!',
            value: resumeThoughtTestId,
          },
          subject: { name: 'subject', type: 'ID', value: clientId },
        },
      },
      queryOptions: { enabled: !!resumeThoughtTestId && !!clientId },
    },
  );

  const draft = ((
    draftData?.data as unknown as {
      thoughtTestDraft?: ThoughtTestDraft;
    }
  )?.thoughtTestDraft ?? (draftData?.data as unknown as ThoughtTestDraft)) as
    | ThoughtTestDraft
    | undefined;

  /**
   * Pour the draft into the form ONCE it arrives. `setFieldsValue` deliberately, not `initialValues`
   * — the form is already mounted by the time the read answers, and it must NOT mark these fields
   * touched: an untouched field is one the therapist did not revisit, and that is exactly what
   * decides which steps are re-fired (see `esThoughtTestBatch`'s `touchedFields`).
   */
  /**
   * WHAT THE PRE-FILL POURED IN, exactly — the baseline "touched" is measured against at Save.
   *
   * antd's own touched flag cannot be used for this. `setFieldsValue` MARKS A REGISTERED FIELD
   * TOUCHED when its value changes (rc-field-form `Field.onStoreChange`: `valueUpdate` with
   * `source: 'external'`), and section 1 is open by default, so `description` is registered when the
   * draft arrives and reads as "the therapist edited it" when nobody did. Fields in collapsed
   * sections escape only because they are not registered yet — which made the answer depend on the
   * race between the draft query and the form mount: the same Continue re-fired the client's
   * situation in one run and left it alone in the next. The re-fire carried identical data, but it
   * stamped the THERAPIST as performer of the client's own situation (cross-surface spec, 2026-09-11).
   *
   * So: remember what was poured in, and at Save call a step touched only if its value now DIFFERS.
   */
  const prefillRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!draft) return;
    const poured = {
      description: draft.description || '',
      // the WHOLE situation — without these the form showed blanks over real answers, and an edit
      // to the description would have written those blanks back (prod, 2026-09-11)
      emotions: draft.emotions ?? [],
      feelings: draft.feelings ?? [],
      bodyPartsFeelings: draft.bodyPartsFeelings ?? 0,
      behaviors: draft.behaviors ?? '',
      automaticThought: draft.automaticThought || '',
      thoughtBelief: draft.thoughtBelief ?? 50,
      /**
       * EVERY ANSWER FROM THE LOG, none from the legacy row (founder, 2026-09-11: *"why do we try
       * to use it if we have granular events that are way more precise?"*).
       *
       * The row is created with `realisticThoughts: [{thought:'',thoughtBelief:50}]` and
       * `result: {50,50}`, so a test abandoned at the evidence step read back as though somebody
       * had already answered both — and a resume would have offered them their own unwritten
       * answers, then saved them as fact. Measured in dev. An ABSENT field here means the step
       * never happened, and the form keeps its own empty default for it.
       */
      ...(draft.thoughtsFor
        ? {
            thoughtsFor: draft.thoughtsFor.map((f, i) => ({
              for: f,
              alternativeThought:
                (draft.thoughtsForAlternativeThoughts ?? [])[i] ?? '',
            })),
          }
        : {}),
      ...(draft.thoughtsAgainst
        ? { thoughtsAgainst: draft.thoughtsAgainst }
        : {}),
      ...(draft.cognitiveDistortions
        ? { cognitiveDistortions: draft.cognitiveDistortions }
        : {}),
      ...(draft.realisticThoughts?.length
        ? { realisticThoughts: draft.realisticThoughts }
        : {}),
      ...(draft.result
        ? {
            result: {
              initialThoughtBelief: draft.result.initialThoughtBelief ?? 50,
              // the log spells it correctly; the FORM keeps the legacy row's `Strenght`
              initialNegativeEmotionStrenght:
                draft.result.initialNegativeEmotionStrength ?? 50,
            },
          }
        : {}),
    };
    prefillRef.current = poured;
    form.setFieldsValue(poured as never);
  }, [draft]);

  const { innerWidth } = useWindowSize();
  const textAlign = (innerWidth || 1000) < 995 ? 'center' : 'left';

  const initialValues = useMemo(() => {
    return {
      thoughtBelief: 50,
      bodyPartsFeelings: 0,
      feelings: [],
      emotions: [],
      behaviors: '',
      // thoughtsFor: [''],
      thoughtsFor: [{ for: '', alternativeThought: '' }],
      thoughtsAgainst: [''],
      realisticThoughts: [{ ...emptyBasicThought }],

      result: {
        initialThoughtBelief: 50,
        initialNegativeEmotionStrenght: 50,
      },
      dayReminders: {
        morning: {
          time: { hours: 9, minutes: 0 },
          enabled: true,
        },
        day: {
          time: { hours: 14, minutes: 0 },
          enabled: true,
        },
        evening: {
          time: { hours: 21, minutes: 0 },
          enabled: true,
        },
      },
    };
  }, []);

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const onSave = async () => {
    /**
     * `true` = EVERY value in the store, not just the MOUNTED fields. Section 11 lives inside a
     * `Collapse` that antd does not render until it is opened, so half this form's reminder state
     * was only reaching the payload via the `merge(initialValues, …)` below — a second mechanism
     * doing the first one's job. It also makes the payload independent of which sections a
     * therapist happened to expand (founder, 2026-09-10).
     */
    const { thoughtsFor, emotions, feelings, result, behaviors, ...values } =
      form.getFieldsValue(true) as CreateActivatingEventForm;

    const remappedValues = {
      ...values,
      emotions: emotions ? emotions : [],
      feelings: feelings ? feelings : [],
      behaviors: behaviors ? behaviors : '',
      result: result ? result : initialValues.result,
      thoughtsFor: thoughtsFor?.map((t) => t.for),
      thoughtsForAlternativeThoughts: thoughtsFor?.map(
        (t) => t.alternativeThought,
      ),
      clientId,
    };

    const mergedValues = merge(initialValues, remappedValues);

    // An ES client's legacy slots go in SILENT — their reminders are the ones they actually set, in
    // the log, through the RRULE surface in section 11. Without this, creating one thought test
    // wrote three reminders (09:00 / 14:00 / 21:00) the therapist never chose and could not see.
    if (isEsClient) {
      mergedValues.dayReminders = withLegacyDaySlotsSilenced(
        mergedValues.dayReminders,
      );
    }

    /**
     * THE ERA DECIDES WHERE THIS TEST IS WRITTEN (E01 portal chapter, founder 2026-09-11).
     *
     * An ES client's test goes through the LOG as the eight facts the board models, and the inline
     * projection writes the same `thoughts` and `activatingEvents` rows in the same transaction — so
     * every existing reader sees exactly what it saw before, and the log finally owns the record.
     * Before this, the therapist's form wrote a legacy row for a converted client and the log
     * learned nothing (`thoughts/17620`, production).
     *
     * A legacy client keeps `createPortalThought`, byte for byte.
     */
    const batch = isEsThoughtTesting
      ? esThoughtTestBatch({
          subject: clientId as string,
          values: mergedValues,
          // the outcome is a RATING somebody gave, not a default that happened to be sitting there
          outcomeRated:
            form.isFieldTouched(['result', 'initialThoughtBelief']) ||
            form.isFieldTouched(['result', 'initialNegativeEmotionStrenght']),
          // CONTINUING one: the same boundary and situation, and only the steps the therapist put a
          // hand on — re-firing an untouched step would supersede it with whatever the form holds.
          ...(draft
            ? {
                thoughtTestId: draft.thoughtTestId,
                activatingEventId: draft.activatingEventId ?? undefined,
                // "touched" = DIFFERS from what the pre-fill poured in (or from the form's own
                // default where the pre-fill left a step absent). Not antd's flag — see `prefillRef`.
                touchedFields: Object.entries(
                  form.getFieldsValue(true) as Record<string, unknown>,
                )
                  .filter(([name, current]) => {
                    const poured = prefillRef.current;
                    if (!poured) return form.isFieldTouched(name);
                    const baseline =
                      name in poured
                        ? poured[name]
                        : (initialValues as Record<string, unknown>)[name];
                    return !isEqual(current, baseline);
                  })
                  .map(([name]) => name),
              }
            : {}),
        })
      : null;

    try {
      const res = await mutate({
        url: '',
        method: 'post',
        values: {},
        meta: batch
          ? {
              operation: 'submitThoughtTestEvents',
              fields: [{ thought: ['_id, updatedAt'] }],
              variables: {
                thoughtTestId: {
                  name: 'thoughtTestId',
                  type: 'ID!',
                  value: batch.thoughtTestId,
                },
                events: {
                  name: 'events',
                  type: '[ThoughtTestEventInput!]!',
                  value: batch.events,
                },
                // the on-behalf handle: WHOSE test this is. `performer` is stamped by the server
                // from the authenticated caller and is deliberately not ours to send.
                subject: { name: 'subject', type: 'ID', value: clientId },
              },
            }
          : {
              operation: 'createPortalThought',
              fields: [{ thought: ['_id, updatedAt'] }],
              variables: {
                input: {
                  name: 'input',
                  type: 'PortalThoughtCreateInput',
                  value: mergedValues,
                },
              },
            },
      });
    } catch (e) {
      console.log(e);
      Sentry.captureException(e);
    }
  };

  useEffect(() => {
    if (createData?.data?.thought?.updatedAt) {
      const handle1 = setTimeout(() => {
        setIsCardFlipped(true);
      }, 1500);

      const handle2 = setTimeout(() => {
        setIsCardFlipped(false);
      }, 4500);

      return () => {
        clearTimeout(handle1);
        clearTimeout(handle2);
      };
    }
  }, [createData?.data?.thought?.updatedAt]);

  useEffect(() => {
    if (createData?.data?.thought?._id) {
      therapistActivitySend({
        type: 'CREATED',
        resource: createData?.data?.thought._id,
      });
    }
  }, [createData?.data?.thought?._id]);

  useActivityTracking(
    createData?.data?.thought?._id || `${dbCols.thoughts}/create`,
  );

  return (
    <Create
      title={
        <div style={{ textWrap: 'wrap' }}>
          {translate('createThought.create')}
        </div>
      }
      saveButtonProps={{
        onClick: onSave,
        disabled:
          isSubmitDisabled ||
          !!(fieldsCount < 10) ||
          !!createData?.data?.thought._id,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '30px',
        }}
      >
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>
      {clientId && (
        <Form<CreateActivatingEventForm>
          form={form}
          initialValues={initialValues}
          onFinish={(values) => {
            console.log('values', values);
          }}
          onFieldsChange={handleFormChange}
          labelCol={{ span: i18n.languages[0]?.startsWith('ru') ? 8 : 6 }}
          wrapperCol={{ span: 10 }}
          labelWrap
          data-testid="cc-create-thought-test-form"
        >
          <Collapse collapsible="header" defaultActiveKey={['1']}>
            <Panel
              header={`1. ${translate('createThought.eventTitle')}`}
              key="1"
            >
              <Form.Item
                name="description"
                label={translate('createThought.eventTitle')}
                required
                rules={[validationRules]}
              >
                <Input.TextArea
                  data-testid="cc-create-thought-event-description"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  size="large"
                  placeholder={translate('createThought.eventLabel')}
                />
              </Form.Item>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`2. ${translate('createThought.emotionsTitle')}`}
              key="2"
            >
              {/**
               * CONTROLLED BY THE STORE'S `emotions` ARRAY, not bound through `Form.Item name`.
               *
               * It used to be bound as `name={['emotions', 'id']}` — which resolves to `undefined`
               * against an array (`[{id, strength}].id`), so antd treated the Select as UNCONTROLLED.
               * That looked fine on a fresh create, where the Select showed whatever had been clicked
               * into it, and was invisible until the first RESUME: a pre-filled `emotions` array
               * rendered its rating sliders below and NO chips up here, because nothing had ever been
               * clicked into the Select's private state (founder, prod 2026-09-11, second report).
               *
               * `setEmotions` is `useWatch('emotions')`, so the chips now follow the store both ways:
               * a click still writes the array through `onChange`, and a pre-fill reads back into it.
               * The `emotions` key stays registered through the `Form.List` below, so the Save gate's
               * field count is unchanged. The old `rules` gated nothing — Save never calls
               * `validateFields` — and are dropped rather than left looking like they did.
               */}
              <Form.Item label={translate('createThought.chooseEmotions')} required>
                <Select
                  data-testid="cc-create-thought-emotions"
                  mode="multiple"
                  value={setEmotions.map((e: { id: number }) => e.id)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={(emotionsIds) => {
                    const eventEmotions =
                      (emotionsIds as unknown as number[]).map((id) => {
                        const emotion = flattenedEmotions.find(
                          (e) => e.id === id,
                        );
                        if (emotion) {
                          const existingEmotion = setEmotions.find(
                            (em) => em.id === id,
                          );
                          return {
                            id: emotion.id,
                            strength: !!existingEmotion
                              ? existingEmotion.strength
                              : 50,
                          };
                        }
                      }) || [];

                    // `touched: true` explicitly — `setFieldValue` does not mark it, and the batch
                    // re-fires the situation ONLY for touched fields. Without this, adding an emotion
                    // on a resume (and touching no slider) would be dropped at Save.
                    form.setFields([
                      { name: 'emotions', value: eventEmotions, touched: true },
                    ]);
                  }}
                >
                  {baseEmotions.map((eg) => {
                    return (
                      <OptGroup label={eg.name} key={eg.name}>
                        {eg.children.map((e) => {
                          return (
                            <Option key={e.id} value={e.id}>
                              {e.name}
                            </Option>
                          );
                        })}
                      </OptGroup>
                    );
                  })}
                </Select>
              </Form.Item>

              {!!setEmotions?.length && (
                <Title level={5} style={{ textAlign }}>
                  {translate('createThought.rateEmotions')}:
                </Title>
              )}

              <Form.List name="emotions">
                {(fields, { add, remove }, errors) => {
                  return (
                    <>
                      {fields.map((field, index) => {
                        return (
                          <div
                            key={field.key}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              justifyContent: 'space-between',
                              maxWidth: 900,
                            }}
                          >
                            <Form.Item
                              {...field}
                              key={'slider' + field.key}
                              name={[field.key, 'strength']}
                              label={
                                // optional chaining as a belt to `preserve`'s braces: a row that
                                // renders one tick before the watch catches up must not throw
                                flattenedEmotions.find(
                                  (e) => e.id === setEmotions[field.key]?.id,
                                )?.name || ''
                              }
                              labelCol={{ span: 10 }}
                              colon
                            >
                              <Slider
                                min={1}
                                max={100}
                                data-testid="cc-create-thought-emotions-slider"
                                style={{ minWidth: 200 }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={'input' + field.key}
                              name={[field.key, 'strength']}
                            >
                              <InputNumber
                                min={1}
                                max={100}
                                data-testid="cc-create-thought-emotions-input"
                              />
                            </Form.Item>
                          </div>
                        );
                      })}
                    </>
                  );
                }}
              </Form.List>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`3. ${translate('createThought.feelingsTitle')}`}
              key="3"
            >
              <Form.Item
                label={translate('createThought.chooseFeelings')}
                name={['feelings']}
                rules={[validationRules]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  data-testid="cc-create-thought-feelings"
                >
                  {baseFeelings.map((bg) => {
                    return (
                      <OptGroup label={bg.name} key={bg.name}>
                        {bg.children.map((e) => {
                          return (
                            <Option key={e.id} value={e.id}>
                              {e.name}
                            </Option>
                          );
                        })}
                      </OptGroup>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item
                name="bodyPartsFeelings"
                label={translate('createThought.feelingsInBody')}
                rules={[validationRules]}
              >
                <Table
                  data-testid="cc-create-thought-body-parts-feelings"
                  columns={feelingsColumns}
                  dataSource={feelingsData}
                  pagination={false}
                  size="small"
                />
              </Form.Item>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`4. ${translate('shared.abcModel.behaviors')}`}
              key="4"
            >
              <Form.Item
                name="behaviors"
                label={translate('shared.abcModel.behaviors')}
              >
                <Input.TextArea
                  data-testid="cc-create-thought-behaviors"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  size="large"
                  placeholder={translate(
                    'shared.abcModel.behaviorsPlaceholder',
                  )}
                />
              </Form.Item>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`5. ${translate('createThought.automaticThoughtTitle')}`}
              key="5"
            >
              <Form.Item
                label={translate('createThought.automaticThoughtTitle')}
                name="automaticThought"
                required
                rules={[validationRules]}
              >
                <Input.TextArea
                  data-testid="cc-create-thought-automatic-thought"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  placeholder={translate('createThought.automaticThoughtTitle')}
                />
              </Form.Item>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                }}
              >
                <Form.Item
                  label={translate('createThought.automaticThoughtBelief')}
                  name={'thoughtBelief'}
                >
                  <Slider
                    min={1}
                    max={100}
                    data-testid="cc-create-thought-automatic-thought-slider"
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>

                <Form.Item name={'thoughtBelief'}>
                  <InputNumber
                    min={1}
                    max={100}
                    data-testid="cc-create-thought-automatic-thought-input"
                  />
                </Form.Item>
              </div>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`6. ${translate('createThought.thoughtsForTitle')}`}
              key="6"
            >
              <Form.List name="thoughtsFor">
                {(fields, { add, remove }, errors) => {
                  return (
                    <>
                      {fields.map((field, index) => {
                        return (
                          <>
                            <Form.Item
                              label={translate('createThought.evidenceFor')}
                              wrapperCol={{
                                offset: 0,
                                span: 10,
                              }}
                              key={field.key}
                              required={index === 0}
                            >
                              <div style={{ display: 'flex' }}>
                                <Form.Item
                                  {...field}
                                  style={{ width: '100%' }}
                                  name={[`${field.name}`, 'for']}
                                  rules={[
                                    {
                                      required: true,
                                      message: translate(
                                        'createThought.thoughtEvidenceRequired',
                                      ),
                                    },
                                  ]}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 4, maxRows: 8 }}
                                    placeholder={translate(
                                      'createThought.evidenceFor',
                                    )}
                                    data-testid="cc-create-thought-evidence-for"
                                  />
                                </Form.Item>

                                {fields.length > 1 ? (
                                  <MinusCircleOutlined
                                    style={{ margin: '5px 0 0 10px' }}
                                    onClick={() => remove(field.name)}
                                    data-testid="cc-create-thought-evidence-for-remove"
                                  />
                                ) : null}
                              </div>
                            </Form.Item>

                            <Form.Item
                              {...field}
                              label={translate(
                                'createThought.alternativeThought',
                              )}
                              name={[`${field.name}`, 'alternativeThought']}
                              tooltip={translate(
                                'createThought.alternativeThoughtTip',
                              )}
                            >
                              <Input.TextArea
                                autoSize={{ minRows: 4, maxRows: 8 }}
                                placeholder={translate(
                                  'createThought.alternativeThought',
                                )}
                                data-testid="cc-create-thought-alternative-thought"
                              />
                            </Form.Item>
                            <Divider />
                          </>
                        );
                      })}

                      <Form.Item wrapperCol={{ offset: 5 }}>
                        <Button
                          type="primary"
                          onClick={() =>
                            add({ for: '', alternativeThought: '' })
                          }
                          icon={<PlusOutlined />}
                          data-testid="cc-create-thought-evidence-for-add"
                        >
                          {translate('createThought.addEvidenceFor')}
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`7. ${translate('createThought.thoughtsAgainstTitle')}`}
              key="7"
            >
              <Form.List name="thoughtsAgainst">
                {(fields, { add, remove }, errors) => {
                  return (
                    <>
                      {fields.map((field, index) => {
                        return (
                          <Form.Item
                            label={
                              index === 0
                                ? translate('createThought.evidenceAgainst')
                                : ''
                            }
                            wrapperCol={{
                              offset: index === 0 ? 0 : 5,
                              span: 10,
                            }}
                            key={field.key}
                            required={index === 0}
                          >
                            <div style={{ display: 'flex' }}>
                              <Form.Item
                                {...field}
                                style={{ width: '100%' }}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      'createThought.evidenceAgainstRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  autoSize={{ minRows: 4, maxRows: 8 }}
                                  placeholder={translate(
                                    'createThought.evidenceAgainst',
                                  )}
                                  data-testid="cc-create-thought-evidence-against"
                                />
                              </Form.Item>
                              {fields.length > 1 ? (
                                <MinusCircleOutlined
                                  style={{ margin: '5px 0 0 10px' }}
                                  onClick={() => remove(field.name)}
                                  data-testid="cc-create-thought-evidence-against-remove"
                                />
                              ) : null}
                            </div>
                          </Form.Item>
                        );
                      })}
                      <Form.Item wrapperCol={{ offset: 5 }}>
                        <Button
                          type="primary"
                          onClick={() => add('')}
                          icon={<PlusOutlined />}
                          data-testid="cc-create-thought-evidence-against-add"
                        >
                          {translate('createThought.addEvidenceAgainst')}
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`8. ${translate(
                'createThought.cognitiveDistortionsTile',
              )}`}
              key="8"
            >
              <Form.Item
                label={translate('createThought.cognitiveDistortions')}
                name="cognitiveDistortions"
                rules={[validationRules]}
              >
                <Select
                  placeholder={translate(
                    'createThought.cognitiveDistortionsPlaceholder',
                  )}
                  optionFilterProp="children"
                  loading={clientsLoading}
                  mode="multiple"
                  allowClear
                  data-testid="cc-create-thought-cognitive-distortions"
                >
                  {Object.keys(translationEn.cognitiveDistorions).map((key) => (
                    <Select.Option key={key} value={key}>
                      {translate(`cognitiveDistorions.${key}`)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`9. ${translate('createThought.realisticThoughtsTitle')}`}
              key="9"
            >
              <Form.List
                name="realisticThoughts"
                // @ts-ignore
                rules={[validationRules]}
              >
                {(fields, { add, remove }, errors) => {
                  return (
                    <>
                      {fields.map((field, index) => {
                        return (
                          <Form.Item
                            label={
                              index === 0
                                ? translate('createThought.realisticThought')
                                : ''
                            }
                            labelCol={{ span: 6 }}
                            wrapperCol={{
                              offset:
                                index === 0
                                  ? 0
                                  : (innerWidth || 0) > 570
                                  ? 5
                                  : 0,
                              span: 14,
                            }}
                            key={field.key}
                            required={index === 0}
                          >
                            <div style={{ display: 'flex' }}>
                              <Form.Item
                                {...field}
                                style={{ width: '100%' }}
                                name={[field.key, 'thought']}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      'createThought.realisticThoughtRequired',
                                    ),
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  autoSize={{ minRows: 4, maxRows: 8 }}
                                  placeholder={translate(
                                    'createThought.realisticThought',
                                  )}
                                  data-testid="cc-create-thought-realistic-thought"
                                />
                              </Form.Item>
                              {fields.length > 1 ? (
                                <MinusCircleOutlined
                                  style={{ margin: '5px 0 0 10px' }}
                                  onClick={() => remove(field.name)}
                                  data-testid="cc-create-thought-realistic-thought-remove"
                                />
                              ) : null}
                            </div>

                            <div
                              key={field.key}
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Form.Item
                                {...field}
                                key={'slider' + field.key}
                                label={translate(
                                  'createThought.automaticThoughtBelief',
                                )}
                                name={[field.key, 'thoughtBelief']}
                                labelCol={{ span: 10 }}
                                colon
                              >
                                <Slider
                                  min={1}
                                  max={100}
                                  data-testid="cc-create-thought-realistic-thought-slider"
                                  style={{ minWidth: 200 }}
                                />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                key={'input' + field.key}
                                name={[field.key, 'thoughtBelief']}
                              >
                                <InputNumber
                                  min={1}
                                  max={100}
                                  data-testid="cc-create-thought-realistic-thought-input"
                                />
                              </Form.Item>
                            </div>
                          </Form.Item>
                        );
                      })}
                      <Form.Item wrapperCol={{ offset: 5 }}>
                        <Button
                          type="primary"
                          onClick={() => add({ ...emptyBasicThought })}
                          icon={<PlusOutlined />}
                          data-testid="cc-create-thought-realistic-thought-add"
                        >
                          {translate('createThought.automaticThoughtAdd')}
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Panel>
          </Collapse>

          <Collapse>
            <Panel
              header={`10. ${translate('createThought.resultsTitle')}`}
              key="10"
            >
              <p>{translate('createThought.resultBelief')}</p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',

                  maxWidth: 900,
                }}
              >
                <Form.Item
                  labelCol={{ span: 10 }}
                  label={translate('createThought.initialThoughtBelief')}
                  name={['result', 'initialThoughtBelief']}
                >
                  <Slider
                    min={1}
                    max={100}
                    data-testid="cc-create-thought-result-belief-slider"
                    style={{ minWidth: 200 }}
                  />
                </Form.Item>
                <Form.Item name={['result', 'initialThoughtBelief']}>
                  <InputNumber
                    min={1}
                    max={100}
                    data-testid="cc-create-thought-result-initial-thought-belief-input"
                  />
                </Form.Item>
              </div>

              <p>{translate('createThought.resultEmotionStrength')}</p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',

                  maxWidth: 900,
                }}
              >
                <Form.Item
                  labelCol={{ span: 10 }}
                  label={translate('createThought.initialEmotionStrength')}
                  name={['result', 'initialNegativeEmotionStrenght']}
                >
                  <Slider
                    min={1}
                    max={100}
                    style={{ minWidth: 200 }}
                    data-testid="cc-create-thought-result-emotion-strength-slider"
                  />
                </Form.Item>
                <Form.Item name={['result', 'initialNegativeEmotionStrenght']}>
                  <InputNumber
                    min={1}
                    max={100}
                    data-testid="cc-create-thought-result-initial-negative-emotion-strength-input"
                  />
                </Form.Item>
              </div>
            </Panel>
          </Collapse>

          {/*
            SECTION 11 IS REPLACED IN PLACE FOR AN ES CLIENT, NOT REMOVED (founder, 2026-09-10:
            "Why did you decide to hide section 11 for es clients? Our mobile app is not hiding
            it."). Mobile swaps the whole screen behind an `EraGate` — legacy
            `CardNotificationsScreen` / ES `CardNotificationsEsScreen` — so a person always finds
            reminders where reminders live; only the contents differ. Deleting the panel here made
            the numbering jump 10 → 12 and took the affordance away with it, which is a worse answer
            to the same problem.

            WHY THE CONTENTS MUST DIFFER. The 3-slot table can express only "a time, on/off" for
            three fixed slots, and for an ES client it does not fail — `updateDayReminders` routes
            into the log via `applyDayRemindersEs`. It rewrites whatever rule the client had to
            `FREQ=DAILY`, so a therapist adjusting the time on a weekly reminder would silently
            destroy the weekly rule. The RRULE surface is the same one mobile's ES screen offers.

            WHY IT WAITS FOR THE SAVE. A schedule points AT a card, so there is nothing to point at
            until the card exists. Mobile never meets this because it opens reminders from a card
            that already exists; a create form does. The panel stays either way, and says so.
          */}
          <Collapse>
            <Panel
              header={`11. ${translate('createThought.timeRemindersTitle')}`}
              key="11"
            >
              {/*
                THE FIELD STAYS MOUNTED IN BOTH ERAS — `hidden`, never unmounted, exactly as the
                coping-card form does. Unmounting it unregisters `dayReminders`, and this screen
                gates Save on `fieldsCount < 10` (registered fields), so for an ES client the count
                fell under the line and the therapist could not save AT ALL. Caught by the E01
                portal e2e, which waits for the era to resolve before saving — the earlier version
                of that spec saved within a second and never saw it.

                It is the same mistake as 7707c531 in the card form wearing different clothes:
                there, unmounting dropped a REQUIRED field from the payload; here it drops a field
                from a COUNT. Both come from treating "do not show this" as "do not render it".
              */}
              <Form.Item
                name="dayReminders"
                label={translate('createThought.timeRemindersTitle')}
                rules={[validationRules]}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 14 }}
                hidden={isEsClient}
              >
                <Table
                  style={{ maxWidth: 500 }}
                  showHeader={false}
                  columns={dayRemindersColumns}
                  dataSource={dayRemindersData}
                  pagination={false}
                  data-testid="cc-create-thought-time-reminders"
                />
              </Form.Item>
              {isEsClient &&
                (createData?.data?.thought?._id ? (
                  <EsCardReminders
                    subject={clientId}
                    targetRef={createData.data.thought._id}
                  />
                ) : (
                  <Text
                    type="secondary"
                    data-testid="cc-create-thought-es-reminders-hint"
                  >
                    {translate('esReminders.afterSave')}
                  </Text>
                ))}
            </Panel>
          </Collapse>

          {!createLoading &&
            (createData?.data?.thought?.updatedAt || !!createError) && (
              <Result
                data-testid="cc-create-thought-result"
                status={createError ? 'error' : 'success'}
                title={
                  createError
                    ? createError.message
                    : translate('createThought.createSuccess')
                }
                extra={
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      alignItems: 'center',
                      maxWidth: 900,
                    }}
                  >
                    {/*
                      Reminders are NOT repeated here — they live in section 11, which is where a
                      reminders section belongs and where it stays in both eras. The save unlocks
                      that panel rather than relocating it.
                    */}
                    <div style={{ width: 400 }}>
                      <ReactCardFlip
                        isFlipped={isCardFlipped}
                        flipDirection="horizontal"
                        flipSpeedBackToFront={2}
                        flipSpeedFrontToBack={2}
                        data-testid="cc-create-thought-card-flip"
                      >
                        <Card
                          data-testid="cc-create-thought-card-flip-front"
                          style={{ width: 400 }}
                          onClick={() => setIsCardFlipped(!isCardFlipped)}
                          bordered
                          hoverable
                          bodyStyle={{
                            minHeight: 200,
                            boxShadow: '0 0 10px #ccc',
                            background: '#45403e',
                            color: '#f5f1f1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 10,
                          }}
                        >
                          {form.getFieldValue('automaticThought')}
                        </Card>

                        <Card
                          data-testid="cc-create-thought-card-flip-back"
                          style={{ width: 400 }}
                          onClick={() => setIsCardFlipped(!isCardFlipped)}
                          bordered
                          hoverable
                          bodyStyle={{
                            minHeight: 200,
                            boxShadow: '0 0 10px #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 10,
                          }}
                        >
                          {form
                            .getFieldValue('realisticThoughts')
                            ?.map((t: any, idx: number) => {
                              return <p key={idx}>{t.thought}</p>;
                            })}
                        </Card>
                      </ReactCardFlip>
                    </div>
                  </div>
                }
              />
            )}
          <Divider />
        </Form>
      )}
    </Create>
  );
};
